import { Box, Divider, useTheme } from "@mui/material";
import { Link, Outlet, useLocation } from "react-router";

import {
  PageLayout,
  RobotoStyledTab,
  RobotoStyledTabs,
} from "@/shared/components";

import { AgentTab, useActiveAgentTab } from "./useActiveAgentTabFromUrl";

/**
 * Top-level Agents page: parent layout for the `/agents/*` route tree.
 * Renders the page chrome (header, tab strip) and delegates the active
 * tab's body to `<Outlet>`.
 *
 * The active tab is derived from the trailing URL segment by
 * `useActiveAgentTab` so deep links into a specific tab survive refresh,
 * and breadcrumbs from descendant pages can target a named tab
 * (`/agents/library`, `/agents/threads`) rather than an index that may
 * redirect elsewhere once additional tabs land.
 *
 * Layout note: the page height flows through a flex chain (page-content →
 * outer Box → tab body Box → outlet) so an inner ``DataTable`` can take
 * the remaining vertical space, matching the Datasets / Events tables.
 */
export function AgentsTabs() {
  const theme = useTheme();
  const [activeTab] = useActiveAgentTab();
  const location = useLocation();

  // Page chrome follows the active tab so the header and browser tab
  // reflect what the user is actually looking at — "Agents" for the
  // library, "Agent Context" for the org-knowledge / MCP form, and
  // "Agent Threads" for the threads list.
  const headerLabel = (() => {
    switch (activeTab) {
      case AgentTab.Context:
        return "Agent Context";
      case AgentTab.Threads:
        return "Agent Threads";
      case AgentTab.Skills:
        return "Agent Skills";
      case AgentTab.Library:
      default:
        return "Agents";
    }
  })();

  return (
    <PageLayout
      title={`Roboto - ${headerLabel}`}
      headerContentLeft={<>{headerLabel}</>}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <div>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <RobotoStyledTabs value={activeTab} aria-label="agents tabs">
              <RobotoStyledTab
                value={AgentTab.Library}
                label="Agents"
                component={Link}
                to={{
                  pathname: AgentTab.Library,
                  search: location.search,
                }}
                replace
              />
              <RobotoStyledTab
                value={AgentTab.Threads}
                label="Threads"
                component={Link}
                to={{
                  pathname: AgentTab.Threads,
                  search: location.search,
                }}
                replace
              />
              <RobotoStyledTab
                value={AgentTab.Skills}
                label="Skills"
                component={Link}
                to={{
                  pathname: AgentTab.Skills,
                  search: location.search,
                }}
                replace
              />
              <RobotoStyledTab
                value={AgentTab.Context}
                label="Context"
                component={Link}
                to={{
                  pathname: AgentTab.Context,
                  search: location.search,
                }}
                replace
              />
            </RobotoStyledTabs>
          </Box>
          <Divider sx={{ borderColor: theme.border.color }} />
        </div>

        <Box
          sx={{
            // ``flex: 1`` + ``minHeight: 0`` together let the tab body fill
            // the remaining page-content height while still letting an
            // inner ``DataTable`` scroll its own overflow. Without
            // ``minHeight: 0`` the flex item refuses to shrink below its
            // content size and the inner ``height: 100%`` chain collapses.
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: theme.spacing(2, 0),
            paddingBottom: theme.spacing(1),
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </PageLayout>
  );
}
